import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Stack,
  Divider,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  Card,
  Chip,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Fade,
  CircularProgress,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import PersonIcon from '@mui/icons-material/Person';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import moment from 'moment';
import {
  getHotelInfo,
  getRoomTypesWithAvailability,
  getGuestStorageKey,
  getGuestProfile,
  createBooking,
  validateNexaStayCoupon,
  initiateNexaStayPayment,
  verifyNexaStayPayment,
  getNexaStayPaymentStatus,
} from './nexastayApi';
import { loadRazorpayScript, computeAmountDueToday, buildRazorpayBranding, buildRazorpayContactPrefill } from './nexastayPayment';
import { useNexaStayTheme } from './NexaStayThemeContext';
import { useNexaStayBasePath, nexastayPath, resolveNexaStayHotelId } from './useNexaStayBasePath';
import { formatDisplayDate, computeRoomPrice, computeRoomPriceFromRateRow, buildInputSx, buildCard3DSx } from './nexastayUtils';
import {
  formatPhoneLengthLabel,
  normalizePhoneInputForCountry,
  resolveCountryNameForInput,
  resolveDialCodeForCountryInput,
  validatePhoneForCountry,
} from '../utils/phoneValidation';
import { resolveGuestPhoneLocale, resolveGuestPhoneLocaleFromProfile } from './nexastayGuestLocale';
import countryCodesData from '../data/country-codes.json';
import countriesData from '../data/countries-states-cities-data';
import { countryFilterOptions, stateFilterOptions, cityFilterOptions } from '../utils/locationSearch';

const CORPORATE_TAX_ID_OPTIONS = [
  { value: 'gstin', label: 'GSTIN' },
  { value: 'pan', label: 'PAN' },
  { value: 'tan', label: 'TAN' },
  { value: 'cin', label: 'CIN' },
  { value: 'other', label: 'Other' },
];

function digitsOnlyPhone(s) {
  return String(s || '').replace(/\D/g, '');
}

/** India: accept 10-digit mobile or 12 digits with leading country code 91. */
function normalizeIndiaMobileDigits(digits) {
  let d = digits;
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return d;
}

function isIndiaDialCode(countryCode) {
  const cc = String(countryCode || '').trim();
  return cc === '+91' || cc === '91';
}

function generateIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const randomNibble = Math.floor(Math.random() * 16);
    const value = char === 'x' ? randomNibble : (randomNibble & 0x3) | 0x8;
    return value.toString(16);
  });
}

const CORPORATE_FIELD_ERRORS_INITIAL = {
  companyName: '',
  companyTaxIdNumber: '',
  companyPhone: '',
  companyEmail: '',
  companyAddress: '',
  country: '',
  state: '',
  city: '',
  pincode: '',
};

function checkoutSessionStorageKey(hotelId) {
  return `nexastay_checkout_${hotelId}`;
}

function extractCheckoutError(err) {
  const data = err?.response?.data;
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail.trim();
  if (typeof data?.error === 'string' && data.error.trim()) return data.error.trim();
  if (typeof data?.message === 'string' && data.message.trim()) return data.message.trim();
  if (Array.isArray(data?.detail)) {
    const parts = data.detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') return item.msg || item.message || JSON.stringify(item);
        return '';
      })
      .filter(Boolean);
    if (parts.length) return parts.join(' ');
  }
  if (typeof err?.message === 'string' && err.message.trim()) return err.message.trim();
  return 'Something went wrong. Please try again or contact the hotel for assistance.';
}

function CheckoutResultDialog({ open, result, theme, symbol, onClose, onViewBookings }) {
  const [justConfirmed, setJustConfirmed] = useState(false);
  const prevStatusRef = useRef(null);

  useEffect(() => {
    if (!open || !result) {
      prevStatusRef.current = null;
      setJustConfirmed(false);
      return;
    }
    const status = result.confirmationStatus || (result.variant === 'success' ? 'confirmed' : null);
    if (prevStatusRef.current === 'pending' && status === 'confirmed') {
      setJustConfirmed(true);
      const timer = setTimeout(() => setJustConfirmed(false), 1400);
      prevStatusRef.current = status;
      return () => clearTimeout(timer);
    }
    prevStatusRef.current = status;
  }, [open, result?.confirmationStatus, result?.variant]);

  if (!result) return null;

  const isWarning = result.variant === 'warning';
  const isError = result.variant === 'error';
  const confirmationStatus =
    result.confirmationStatus || (result.variant === 'success' ? 'confirmed' : null);
  const isPending = confirmationStatus === 'pending';
  const isConfirmed = result.variant === 'success' && confirmationStatus === 'confirmed';
  const isSuccess = isPending || isConfirmed;

  const confirmPop = keyframes`
    0% { transform: scale(0.55); opacity: 0.35; }
    55% { transform: scale(1.12); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  `;
  const pulseRing = keyframes`
    0% { box-shadow: 0 0 0 0 ${alpha(theme.accent, 0.45)}; }
    70% { box-shadow: 0 0 0 14px ${alpha(theme.accent, 0)}; }
    100% { box-shadow: 0 0 0 0 ${alpha(theme.accent, 0)}; }
  `;

  const pendingColor = '#ed6c02';
  const accentColor = isPending
    ? pendingColor
    : isConfirmed
      ? theme.accent
      : isWarning
        ? pendingColor
        : theme.mode === 'dark'
          ? '#f44336'
          : '#d32f2f';

  const headerTitle =
    result.title ||
    (isPending
      ? 'Confirming booking'
      : isConfirmed
        ? 'Booking confirmed'
        : isWarning
          ? 'Payment cancelled'
          : 'Booking failed');

  const StatusIcon = isPending ? HourglassTopIcon : isConfirmed ? CheckCircleIcon : isWarning ? CancelOutlinedIcon : ErrorOutlineIcon;

  const balanceAtHotel = Math.max(0, Number(result.balance_at_hotel ?? 0));
  const amountPaid = Math.max(0, Number(result.amount_paid ?? 0));
  const totalAmount = Math.max(0, Number(result.total_amount ?? 0));

  return (
    <Dialog
      open={open}
      onClose={isPending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(accentColor, 0.35)}`,
          boxShadow: `0 24px 64px ${alpha('#000', 0.45)}, 0 0 48px ${alpha(accentColor, 0.14)}`,
          bgcolor: theme.paper,
          transition: 'border-color 0.45s ease, box-shadow 0.45s ease',
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.75,
          background: isConfirmed
            ? `linear-gradient(135deg, ${alpha(theme.accent, 0.24)} 0%, ${alpha(theme.accent, 0.05)} 100%)`
            : isPending
              ? `linear-gradient(135deg, ${alpha(pendingColor, 0.2)} 0%, ${alpha(pendingColor, 0.04)} 100%)`
              : `linear-gradient(135deg, ${alpha(accentColor, 0.18)} 0%, ${alpha(accentColor, 0.04)} 100%)`,
          borderBottom: `1px solid ${alpha(accentColor, 0.22)}`,
          transition: 'background 0.5s ease, border-color 0.45s ease',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.75}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(accentColor, 0.18),
              color: accentColor,
              boxShadow: `0 10px 28px ${alpha(accentColor, 0.28)}`,
              animation: isPending
                ? `${pulseRing} 1.6s ease-out infinite`
                : justConfirmed
                  ? `${confirmPop} 0.55s cubic-bezier(0.34, 1.56, 0.64, 1)`
                  : 'none',
              transition: 'background-color 0.45s ease, color 0.45s ease',
            }}
          >
            {isPending ? (
              <HourglassTopIcon sx={{ fontSize: 32, animation: 'spin 1.2s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
            ) : (
              <StatusIcon sx={{ fontSize: 34 }} />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 0.35 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: theme.textPrimary, lineHeight: 1.2 }}>
                {headerTitle}
              </Typography>
              {isSuccess ? (
                <Chip
                  size="small"
                  label={isPending ? 'Pending' : 'Confirmed'}
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    bgcolor: alpha(accentColor, 0.14),
                    color: accentColor,
                    border: `1px solid ${alpha(accentColor, 0.35)}`,
                    transition: 'all 0.4s ease',
                  }}
                />
              ) : null}
            </Stack>
            <Typography variant="body2" sx={{ color: theme.textSecondary }}>
              {result.message}
            </Typography>
            {isPending ? (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <CircularProgress size={14} sx={{ color: pendingColor }} />
                <Typography variant="caption" sx={{ color: theme.textSecondary }}>
                  Creating your reservation…
                </Typography>
              </Stack>
            ) : null}
          </Box>
        </Stack>
      </Box>

      {isSuccess ? (
        <DialogContent sx={{ pt: 2.5, px: 3 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2.5,
              bgcolor: theme.mode === 'dark' ? 'rgba(0,0,0,0.22)' : 'rgba(0,0,0,0.03)',
              borderColor: alpha(isConfirmed ? theme.accent : pendingColor, 0.22),
              transition: 'border-color 0.45s ease',
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography variant="body2" sx={{ color: theme.textSecondary }}>Booking code</Typography>
                {isConfirmed && (result.booking_code || result.booking_id) ? (
                  <Chip
                    label={result.booking_code || result.booking_id}
                    sx={{
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      bgcolor: alpha(theme.accent, 0.12),
                      color: theme.accent,
                      border: `1px solid ${alpha(theme.accent, 0.3)}`,
                      animation: justConfirmed ? `${confirmPop} 0.5s ease` : 'none',
                    }}
                  />
                ) : (
                  <Typography variant="body2" sx={{ color: theme.textMuted, fontStyle: 'italic' }}>
                    Assigning…
                  </Typography>
                )}
              </Stack>
              <Divider sx={{ borderColor: theme.border }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" sx={{ color: theme.textSecondary }}>Check-in</Typography>
                <Typography variant="body2" sx={{ color: theme.textPrimary, fontWeight: 600 }}>
                  {formatDisplayDate(result.check_in)}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" sx={{ color: theme.textSecondary }}>Check-out</Typography>
                <Typography variant="body2" sx={{ color: theme.textPrimary, fontWeight: 600 }}>
                  {formatDisplayDate(result.check_out)}
                </Typography>
              </Stack>
              <Divider sx={{ borderColor: theme.border }} />
              {amountPaid > 0 ? (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" sx={{ color: theme.textSecondary }}>Paid online</Typography>
                  <Typography variant="body2" sx={{ color: theme.accent, fontWeight: 700 }}>
                    {symbol}{amountPaid.toFixed(2)}
                  </Typography>
                </Stack>
              ) : null}
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" sx={{ color: theme.textSecondary }}>
                  {result.payment_method === 'pay_at_hotel' ? 'Due at check-in' : balanceAtHotel > 0 ? 'Balance at hotel' : 'Total paid'}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: theme.textPrimary }}>
                  {symbol}{(result.payment_method === 'pay_at_hotel' ? totalAmount : balanceAtHotel > 0 ? balanceAtHotel : totalAmount).toFixed(2)}{' '}
                  {result.currency || 'INR'}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </DialogContent>
      ) : (
        <DialogContent sx={{ pt: 2.5, px: 3 }}>
          <Alert
            severity={isWarning ? 'warning' : 'error'}
            variant="outlined"
            sx={{ borderRadius: 2, bgcolor: alpha(accentColor, 0.06) }}
          >
            {isWarning
              ? 'No payment was taken. You can try again or choose pay at hotel.'
              : 'If money was deducted from your account, please save your payment reference and contact the hotel.'}
          </Alert>
        </DialogContent>
      )}

      <DialogActions sx={{ px: 3, pb: 3, pt: isSuccess ? 0 : 1, gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isPending}
          sx={{ borderColor: theme.border, color: theme.textSecondary, fontWeight: 600 }}
        >
          {isPending ? 'Please wait…' : 'Close'}
        </Button>
        {isConfirmed ? (
          <Button
            variant="contained"
            startIcon={<ListAltOutlinedIcon />}
            onClick={onViewBookings}
            sx={{
              bgcolor: theme.accent,
              color: theme.mode === 'dark' ? '#121212' : '#000',
              fontWeight: 700,
              boxShadow: `0 8px 24px ${alpha(theme.accent, 0.35)}`,
              '&:hover': { bgcolor: theme.accent },
            }}
          >
            View my bookings
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

function CheckoutSectionHeader({ icon: Icon, title, subtitle, theme, action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 2 }}>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(145deg, ${alpha(theme.accent, 0.32)} 0%, ${alpha(theme.accent, 0.08)} 100%)`,
            border: `1px solid ${alpha(theme.accent, 0.35)}`,
            boxShadow: `0 4px 14px ${alpha(theme.accent, 0.2)}`,
            color: theme.accent,
          }}
        >
          <Icon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.textPrimary, lineHeight: 1.25 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" sx={{ color: theme.textSecondary, display: 'block', mt: 0.25 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {action || null}
    </Box>
  );
}

function CheckoutPaymentOption({ selected, onSelect, icon: Icon, title, amountLabel, hint, theme, badge }) {
  return (
    <Paper
      elevation={0}
      onClick={onSelect}
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      sx={{
        flex: 1,
        minWidth: 0,
        p: 1.75,
        cursor: 'pointer',
        borderRadius: 2.5,
        position: 'relative',
        overflow: 'hidden',
        border: selected ? `2px solid ${theme.accent}` : `1px solid ${alpha(theme.border, 0.9)}`,
        background: selected
          ? `linear-gradient(155deg, ${alpha(theme.accent, theme.mode === 'dark' ? 0.22 : 0.14)} 0%, ${alpha(theme.accent, 0.04)} 100%)`
          : `linear-gradient(180deg, ${alpha(theme.paper, 0.95)} 0%, ${alpha(theme.bg, 0.4)} 100%)`,
        boxShadow: selected
          ? `0 10px 28px ${alpha(theme.accent, 0.28)}, inset 0 1px 0 ${alpha('#fff', 0.08)}`
          : `inset 0 1px 0 ${alpha('#fff', 0.04)}`,
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: selected ? 'translateY(-2px) scale(1.01)' : 'none',
        '&:hover': {
          borderColor: theme.accent,
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 22px ${alpha(theme.accent, 0.18)}`,
        },
        '&::before': selected
          ? {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: `linear-gradient(90deg, ${theme.accent}, ${alpha(theme.accent, 0.45)})`,
            }
          : {},
      }}
    >
      {selected ? (
        <CheckCircleIcon
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            fontSize: 20,
            color: theme.accent,
            filter: `drop-shadow(0 2px 6px ${alpha(theme.accent, 0.45)})`,
          }}
        />
      ) : null}
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            bgcolor: selected ? alpha(theme.accent, 0.24) : alpha(theme.accent, 0.1),
            color: theme.accent,
            border: `1px solid ${alpha(theme.accent, selected ? 0.45 : 0.2)}`,
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, pr: selected ? 2.5 : 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" sx={{ mb: 0.35 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.textPrimary,
                fontWeight: 700,
                letterSpacing: 0.3,
                textTransform: 'uppercase',
                fontSize: '0.68rem',
              }}
            >
              {title}
            </Typography>
            {badge ? (
              <Chip
                label={badge}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  bgcolor: alpha(theme.accent, 0.12),
                  color: theme.accent,
                }}
              />
            ) : null}
          </Stack>
          <Typography variant="subtitle2" sx={{ color: theme.accent, fontWeight: 800, lineHeight: 1.25, mb: 0.25 }}>
            {amountLabel}
          </Typography>
          {hint ? (
            <Typography variant="caption" sx={{ color: theme.textSecondary, lineHeight: 1.35, display: 'block' }}>
              {hint}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

export default function CheckoutPage() {
  const { hotelId: routeHotelId } = useParams();
  const hotelId = resolveNexaStayHotelId(routeHotelId);
  const basePath = useNexaStayBasePath();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const checkoutContextRestoredRef = useRef(false);
  const [hotel, setHotel] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('pay_at_hotel');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [coupon, setCoupon] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponDiscountPercent, setCouponDiscountPercent] = useState(0);
  const [couponMessage, setCouponMessage] = useState('');
  const [couponApplying, setCouponApplying] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestNameTouched, setGuestNameTouched] = useState(false);
  const [guestNameError, setGuestNameError] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestPhoneCountryCode, setGuestPhoneCountryCode] = useState('');
  const [guestCountry, setGuestCountry] = useState('');
  const [guestCountryTouched, setGuestCountryTouched] = useState(false);
  const [guestPhoneCountryCodeTouched, setGuestPhoneCountryCodeTouched] = useState(false);
  const [guestPhoneTouched, setGuestPhoneTouched] = useState(false);
  const [guestPhoneError, setGuestPhoneError] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestEmailTouched, setGuestEmailTouched] = useState(false);
  const [guestEmailError, setGuestEmailError] = useState('');
  const [bookingCustomerType, setBookingCustomerType] = useState('individual');
  const [guestNote, setGuestNote] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyTaxIdType, setCompanyTaxIdType] = useState('gstin');
  const [companyTaxIdNumber, setCompanyTaxIdNumber] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyCity, setCompanyCity] = useState('');
  const [companyState, setCompanyState] = useState('');
  const [companyCountry, setCompanyCountry] = useState('');
  const [companyPincode, setCompanyPincode] = useState('');
  const [corporateDialogOpen, setCorporateDialogOpen] = useState(false);
  /** Ref: 'new' = first Corporate pick (cancel → Individual); 'edit' = cancel only closes dialog. */
  const corporateDialogIntentRef = useRef(null);
  const [corporateFieldErrors, setCorporateFieldErrors] = useState(CORPORATE_FIELD_ERRORS_INITIAL);
  const [expectedArrivalTime, setExpectedArrivalTime] = useState(null);
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [checkoutResult, setCheckoutResult] = useState(null);
  const submitIdempotencyKeyRef = useRef(null);
  const [phoneCountryCodeMenuOpen, setPhoneCountryCodeMenuOpen] = useState(false);
  const [phoneCountryCodeSearch, setPhoneCountryCodeSearch] = useState('');
  const phoneCountryCodeAnchorRef = useRef(null);
  const countryCodes = useMemo(() => countryCodesData, []);
  const guestCountries = useMemo(
    () => (Array.isArray(countriesData?.countries) ? countriesData.countries : []),
    []
  );
  const selectedGuestCountryObj = useMemo(() => {
    if (!guestCountries.length || !String(guestCountry || '').trim()) return null;
    const raw = String(guestCountry).trim();
    return (
      guestCountries.find(
        (c) =>
          c?.name === raw ||
          c?.code === raw ||
          (c?.name && c.name.toLowerCase() === raw.toLowerCase())
      ) || null
    );
  }, [guestCountries, guestCountry]);
  const applyDialCodeForCountry = useCallback(
    (countryInput) => {
      const dialCode = resolveDialCodeForCountryInput(countryInput, countryCodes, guestCountries);
      if (dialCode) setGuestPhoneCountryCode(dialCode);
    },
    [countryCodes, guestCountries]
  );
  const selectedPhoneCountryCode = useMemo(
    () =>
      countryCodes.find((cc) => cc.dialCode === guestPhoneCountryCode) ||
      countryCodes.find((cc) => cc.code === 'IN'),
    [countryCodes, guestPhoneCountryCode]
  );

  const validateName = useCallback((name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return 'Name is required';
    if (trimmed.length < 2) return 'Name must be at least 2 characters';
    if (trimmed.length > 100) return 'Name must be 100 characters or less';
    if (!/^[\p{L}\p{M}\s.'-]+$/u.test(trimmed)) return 'Name can only contain letters, spaces, and common punctuation';
    return '';
  }, []);

  const validatePhone = useCallback((phone, countryCode) => {
    return validatePhoneForCountry(phone, countryCode || '+91', {
      required: true,
      label: 'Phone number',
    });
  }, []);

  const validateEmail = useCallback((email) => {
    const v = String(email || '').trim();
    if (!v) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Enter a valid email address';
    return '';
  }, []);

  useEffect(() => {
    if (guestPhoneTouched || termsAccepted) {
      setGuestPhoneError(validatePhone(guestPhone, guestPhoneCountryCode));
    }
  }, [guestPhoneCountryCode, guestPhone, guestPhoneTouched, termsAccepted, validatePhone]);

  useEffect(() => {
    if (guestEmailTouched) setGuestEmailError(validateEmail(guestEmail));
  }, [guestEmail, guestEmailTouched, validateEmail]);

  const dismissCorporateDialog = useCallback((revertIfNew) => {
    const intent = corporateDialogIntentRef.current;
    corporateDialogIntentRef.current = null;
    setCorporateDialogOpen(false);
    setCorporateFieldErrors(CORPORATE_FIELD_ERRORS_INITIAL);
    if (revertIfNew && intent === 'new') {
      setBookingCustomerType('individual');
    }
  }, []);

  const checkIn = searchParams.get('check_in') || '';
  const checkOut = searchParams.get('check_out') || '';
  const roomsJsonStr = searchParams.get('rooms_json') || '[]';
  const ratePlanId = searchParams.get('rate_plan_id') || null;

  // Persist checkout URL params so Google OAuth (which used to drop the query string) can be recovered.
  useEffect(() => {
    if (!hotelId || !checkIn || !checkOut) return;
    try {
      sessionStorage.setItem(
        checkoutSessionStorageKey(hotelId),
        JSON.stringify({
          check_in: checkIn,
          check_out: checkOut,
          rooms_json: roomsJsonStr || '[]',
          rate_plan_id: ratePlanId || '',
        })
      );
    } catch (_) {}
  }, [hotelId, checkIn, checkOut, roomsJsonStr, ratePlanId]);

  useEffect(() => {
    if (!hotelId || checkoutContextRestoredRef.current) return;
    if (checkIn && checkOut) return;
    try {
      const raw = sessionStorage.getItem(checkoutSessionStorageKey(hotelId));
      if (!raw) return;
      const o = JSON.parse(raw);
      if (!o?.check_in || !o?.check_out) return;
      checkoutContextRestoredRef.current = true;
      const p = new URLSearchParams();
      p.set('check_in', String(o.check_in));
      p.set('check_out', String(o.check_out));
      if (o.rooms_json) p.set('rooms_json', typeof o.rooms_json === 'string' ? o.rooms_json : JSON.stringify(o.rooms_json));
      if (o.rate_plan_id) p.set('rate_plan_id', String(o.rate_plan_id));
      navigate(`${location.pathname}?${p.toString()}`, { replace: true });
    } catch (_) {}
  }, [hotelId, checkIn, checkOut, navigate, location.pathname]);
  const roomOccupancies = useMemo(() => {
    try {
      const str = roomsJsonStr.startsWith('%') ? decodeURIComponent(roomsJsonStr) : roomsJsonStr;
      return JSON.parse(str);
    } catch {
      return [];
    }
  }, [roomsJsonStr]);

  const [editableRoomOccupancies, setEditableRoomOccupancies] = useState([]);

  useEffect(() => {
    if (roomOccupancies?.length) {
      setEditableRoomOccupancies(
        roomOccupancies.map((r, i) => ({
          roomTypeId: r.roomTypeId,
          roomIndex: r.roomIndex ?? i,
          ratePlanId: r.ratePlanId ?? null,
          adults: r.adults ?? 1,
          children: r.children ?? 0,
          infants: r.infants ?? 0,
        }))
      );
    } else {
      setEditableRoomOccupancies([]);
    }
  }, [roomsJsonStr]);

  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 1;
    return Math.max(1, Math.floor((new Date(checkOut) - new Date(checkIn)) / (24 * 60 * 60 * 1000)));
  }, [checkIn, checkOut]);

  /** When any room has ratePlanId we need all rate plans to resolve per-room rates. */
  const fetchAllRatePlans = useMemo(
    () => editableRoomOccupancies.some((r) => r.ratePlanId != null),
    [editableRoomOccupancies]
  );

  useEffect(() => {
    if (!hotelId) return;
    setLoading(true);
    const rpId = fetchAllRatePlans ? null : ratePlanId;
    const allPlans = fetchAllRatePlans;
    Promise.allSettled([
      getHotelInfo(hotelId),
      getRoomTypesWithAvailability(hotelId, checkIn, checkOut, rpId, allPlans),
    ])
      .then((results) => {
        const [hRes, aRes] = results;
        if (hRes.status === 'fulfilled') {
          setHotel(hRes.value);
        } else {
          setHotel(null);
        }
        if (aRes.status === 'fulfilled') {
          setAvailability(aRes.value);
        } else {
          setAvailability(null);
        }
      })
      .finally(() => setLoading(false));
  }, [hotelId, checkIn, checkOut, ratePlanId, fetchAllRatePlans]);

  // Default guest country + dial code: visitor location, then hotel; skipped when logged-in (profile effect).
  useEffect(() => {
    if (!hotel || guestCountryTouched || guestPhoneCountryCodeTouched) return;
    let guestId = null;
    try {
      guestId = localStorage.getItem(getGuestStorageKey(hotelId));
    } catch (_) {
      guestId = null;
    }
    if (guestId) return;

    const locale = resolveGuestPhoneLocale({
      visitorCountry: hotel.visitor_country,
      visitorCountryIso: hotel.visitor_country_iso,
      hotelCountry: hotel.country,
      countryCodes,
      guestCountries,
    });
    if (locale.countryName) setGuestCountry(locale.countryName);
    if (locale.dialCode) setGuestPhoneCountryCode(locale.dialCode);
  }, [
    hotel,
    hotelId,
    guestCountryTouched,
    guestPhoneCountryCodeTouched,
    countryCodes,
    guestCountries,
  ]);

  useEffect(() => {
    if (!hotel?.country) return;
    const list = Array.isArray(countriesData?.countries) ? countriesData.countries : [];
    setCompanyCountry((prev) => {
      if (String(prev || '').trim()) return prev;
      const raw = String(hotel.country).trim();
      const byCode = list.find((c) => c.code === raw);
      if (byCode?.name) return byCode.name;
      const byName = list.find(
        (c) => c.name === raw || (c.name && c.name.toLowerCase() === raw.toLowerCase())
      );
      if (byName?.name) return byName.name;
      return raw;
    });
  }, [hotel?.country]);

  const companyCountries = useMemo(
    () => (Array.isArray(countriesData?.countries) ? countriesData.countries : []),
    []
  );

  const selectedCompanyCountryObj = useMemo(() => {
    if (!companyCountries.length || !String(companyCountry || '').trim()) return null;
    const raw = String(companyCountry).trim();
    return (
      companyCountries.find(
        (c) =>
          c?.name === raw ||
          c?.code === raw ||
          (c?.name && c.name.toLowerCase() === raw.toLowerCase())
      ) || null
    );
  }, [companyCountries, companyCountry]);

  const companyStates = selectedCompanyCountryObj?.states || [];

  const selectedCompanyStateObj = useMemo(() => {
    if (!companyStates.length || !String(companyState || '').trim()) return null;
    return companyStates.find((s) => s?.name === companyState) || null;
  }, [companyStates, companyState]);

  const companyCities = selectedCompanyStateObj?.cities || [];

  const validateCorporateFields = useCallback(
    (statesList, citiesList) => {
      const e = { ...CORPORATE_FIELD_ERRORS_INITIAL };
      const name = String(companyName).trim();
      if (!name) e.companyName = 'Company name is required';
      else if (name.length < 2) e.companyName = 'Enter at least 2 characters';

      const tax = String(companyTaxIdNumber).trim();
      if (!tax) e.companyTaxIdNumber = 'Tax ID number is required';

      const ph = digitsOnlyPhone(companyPhone);
      if (ph && (ph.length < 6 || ph.length > 15)) {
        e.companyPhone = 'Enter a valid phone number (6–15 digits)';
      }

      const em = String(companyEmail).trim();
      if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
        e.companyEmail = 'Enter a valid email address';
      }

      const addr = String(companyAddress).trim();
      if (addr && addr.length < 3) {
        e.companyAddress = 'Enter a complete billing address';
      }

      if (String(companyCountry).trim() && !selectedCompanyCountryObj) {
        e.country = 'Choose a valid country from the list';
      }
      if (selectedCompanyCountryObj && statesList.length > 0 && String(companyState).trim() && !selectedCompanyStateObj) {
        e.state = 'Choose a valid state from the list';
      }
      if (selectedCompanyStateObj && citiesList.length > 0 && String(companyCity).trim()) {
        const cityOk = citiesList.some((c) => c === companyCity);
        if (!cityOk) e.city = 'Choose a valid city from the list';
      }

      const pin = String(companyPincode).trim();
      if (pin && (pin.length < 3 || pin.length > 12)) {
        e.pincode = 'Enter a valid PIN or ZIP code';
      }

      return e;
    },
    [
      companyName,
      companyTaxIdNumber,
      companyPhone,
      companyEmail,
      companyAddress,
      companyCountry,
      companyState,
      companyCity,
      companyPincode,
      selectedCompanyCountryObj,
      selectedCompanyStateObj,
    ]
  );

  const saveCorporateDialog = useCallback(() => {
    const errs = validateCorporateFields(companyStates, companyCities);
    setCorporateFieldErrors(errs);
    if (Object.values(errs).some(Boolean)) return;
    corporateDialogIntentRef.current = null;
    setCorporateDialogOpen(false);
  }, [validateCorporateFields, companyStates, companyCities]);

  // If user is logged in (guest for this hotel), prefill name, phone, email from profile
  useEffect(() => {
    if (!hotelId) return;
    let guestId;
    try {
      guestId = localStorage.getItem(getGuestStorageKey(hotelId));
    } catch (_) {
      return;
    }
    if (!guestId) return;
    getGuestProfile(hotelId, guestId)
      .then((profile) => {
        if (profile?.full_name) setGuestName(profile.full_name.trim());
        if (profile?.email) setGuestEmail(String(profile.email).trim());
        const locale = resolveGuestPhoneLocaleFromProfile(profile, {
          visitorCountry: hotel?.visitor_country,
          visitorCountryIso: hotel?.visitor_country_iso,
          hotelCountry: hotel?.country,
          countryCodes,
          guestCountries,
        });
        if (profile?.phone != null && profile.phone !== '') {
          setGuestPhone(
            normalizePhoneInputForCountry(String(profile.phone).replace(/\D/g, ''), locale.dialCode)
          );
        }
        if (!guestPhoneCountryCodeTouched && locale.dialCode) {
          setGuestPhoneCountryCode(locale.dialCode);
        }
        if (!guestCountryTouched && locale.countryName) {
          setGuestCountry(locale.countryName);
        }
      })
      .catch(() => {});
  }, [
    hotelId,
    hotel?.visitor_country,
    hotel?.visitor_country_iso,
    hotel?.country,
    countryCodes,
    guestCountries,
    guestCountryTouched,
    guestPhoneCountryCodeTouched,
  ]);

  // Default expected arrival from hotel.check_in_time; date = stay check-in or today if URL params missing (e.g. after broken OAuth redirect).
  useEffect(() => {
    let day = moment();
    if (checkIn) {
      const baseDate = moment(checkIn, [moment.ISO_8601, 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss'], true);
      if (baseDate.isValid()) day = baseDate;
    }
    let hour = 14;
    let minute = 0;
    const raw = hotel?.check_in_time;
    if (raw != null && String(raw).trim() !== '') {
      const timeStr = String(raw).trim();
      const hhmm = timeStr.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
      if (hhmm) {
        hour = parseInt(hhmm[1], 10);
        minute = parseInt(hhmm[2], 10);
      } else {
        const parsed = moment(timeStr, ['HH:mm', 'HH:mm:ss', 'h:mm A', 'h:mm a', 'hh:mm A'], true);
        if (parsed.isValid()) {
          hour = parsed.hour();
          minute = parsed.minute();
        }
      }
    }
    const value = day.clone().set({ hour, minute, second: 0, millisecond: 0 });
    if (value.isValid()) setExpectedArrivalTime(value);
  }, [checkIn, hotel]);

  const theme = useNexaStayTheme();
  const mainBgSx = { minHeight: '100vh', bgcolor: theme.bg };
  const inputSx = useMemo(() => buildInputSx(theme), [theme]);
  const card3DSx = useMemo(() => buildCard3DSx(theme), [theme]);
  const symbol = hotel?.currency === 'INR' ? '₹' : hotel?.currency || '₹';
  const gstIncluded = hotel?.gst_included === true;
  const taxRate = gstIncluded && hotel?.tax_percentage != null ? Number(hotel.tax_percentage) / 100 : 0;
  const taxPercentageDisplay = gstIncluded && hotel?.tax_percentage != null ? Number(hotel.tax_percentage) : 0;

  const paymentOptions = hotel?.payment_options || {};
  const availablePaymentMethods = useMemo(() => {
    const methods = paymentOptions.available_methods;
    if (Array.isArray(methods) && methods.length > 0) return methods;
    return ['pay_at_hotel'];
  }, [paymentOptions.available_methods]);
  const partialPayPercent = Number(paymentOptions.partial_pay_percent) || 30;

  useEffect(() => {
    if (!availablePaymentMethods.length) return;
    if (!availablePaymentMethods.includes(paymentMethod)) {
      setPaymentMethod(availablePaymentMethods[0]);
    }
  }, [availablePaymentMethods, paymentMethod]);

  const roomTypesMap = useMemo(() => {
    const list = availability?.room_types || [];
    return list.reduce((m, rt) => ({ ...m, [rt.id]: rt }), {});
  }, [availability]);

  const { roomTotal, discount, tax, payableAmount, roomDetails } = useMemo(() => {
    let roomTotal = 0;
    const details = [];
    editableRoomOccupancies.forEach(({ roomTypeId, roomIndex, ratePlanId: occRatePlanId, adults, children, infants }) => {
      const rt = roomTypesMap[roomTypeId];
      if (rt) {
        const baseAdults = rt.base_adults != null ? Number(rt.base_adults) : 1;
        const baseChildren = rt.base_children != null ? Number(rt.base_children) : 0;
        const occAdults = adults ?? baseAdults;
        const occChildren = children ?? baseChildren;
        let price;
        let basePrice;
        let extraAdultAmount = 0;
        let extraChildAmount = 0;
        const extraAdultsCount = Math.max(0, occAdults - baseAdults);
        const extraChildrenCount = Math.max(0, occChildren - baseChildren);

        if (occRatePlanId != null && Array.isArray(rt.rate_plan_rates)) {
          const rateRow = rt.rate_plan_rates.find((r) => String(r.rate_plan?.id) === String(occRatePlanId));
          price = computeRoomPriceFromRateRow(rt, rateRow, { adults: occAdults, children: occChildren, infants: infants ?? 0 }, nights);
          basePrice = computeRoomPriceFromRateRow(rt, rateRow, { adults: baseAdults, children: baseChildren, infants: 0 }, nights);
          const extraAdultRate = rateRow?.extra_adult_rate ?? rt.extra_adult_rate ?? 0;
          const extraChildRate = rateRow?.extra_child_rate ?? rt.extra_child_rate ?? 0;
          extraAdultAmount = extraAdultsCount * (extraAdultRate * nights);
          extraChildAmount = extraChildrenCount * (extraChildRate * nights);
        } else {
          price = computeRoomPrice(rt, { adults: occAdults, children: occChildren, infants: infants ?? 0 }, nights);
          basePrice = computeRoomPrice(rt, { adults: baseAdults, children: baseChildren, infants: 0 }, nights);
          const extraAdultRate = rt.extra_adult_rate ?? 0;
          const extraChildRate = rt.extra_child_rate ?? 0;
          extraAdultAmount = extraAdultsCount * (extraAdultRate * nights);
          extraChildAmount = extraChildrenCount * (extraChildRate * nights);
        }
        roomTotal += price;
        const rateRow = occRatePlanId != null && Array.isArray(rt.rate_plan_rates)
          ? rt.rate_plan_rates.find((r) => String(r.rate_plan?.id) === String(occRatePlanId))
          : null;
        const planName = rateRow?.rate_plan?.name || rateRow?.rate_plan?.code || (occRatePlanId != null ? `Plan ${occRatePlanId}` : null);
        details.push({
          rt,
          adults: occAdults,
          children: occChildren,
          infants: infants ?? 0,
          price,
          basePrice,
          extraAdultAmount,
          extraChildAmount,
          extraAdultsCount,
          extraChildrenCount,
          ratePlanId: occRatePlanId ?? null,
          planName,
        });
      }
    });
    const baseTotal = roomTotal;
    const safeDiscountPercent = Math.max(0, Math.min(100, Number(couponDiscountPercent) || 0));
    const discountVal = couponApplied ? (baseTotal * safeDiscountPercent) / 100 : 0;
    const afterDiscount = baseTotal - discountVal;
    const taxVal = afterDiscount * taxRate;
    const gross = afterDiscount + taxVal;
    const payableAmount = Math.round(gross * 100) / 100;
    return {
      roomTotal: baseTotal,
      discount: discountVal,
      tax: taxVal,
      payableAmount,
      roomDetails: details,
    };
  }, [editableRoomOccupancies, roomTypesMap, nights, couponApplied, couponDiscountPercent, taxRate]);

  const amountDueToday = useMemo(
    () => computeAmountDueToday(payableAmount, paymentMethod, partialPayPercent),
    [payableAmount, paymentMethod, partialPayPercent]
  );

  const balanceAtHotel = useMemo(
    () => Math.max(0, Math.round((payableAmount - amountDueToday) * 100) / 100),
    [payableAmount, amountDueToday]
  );

  const buildPendingCheckoutResult = useCallback(
    (razorpayOrderId) => ({
      variant: 'success',
      confirmationStatus: 'pending',
      title: 'Confirming booking',
      message: 'Payment received. Creating your reservation…',
      razorpay_order_id: razorpayOrderId,
      check_in: checkIn,
      check_out: checkOut,
      total_amount: payableAmount,
      amount_paid: amountDueToday,
      balance_at_hotel: balanceAtHotel,
      payment_method: paymentMethod,
      currency: hotel?.currency || 'INR',
    }),
    [
      checkIn,
      checkOut,
      payableAmount,
      amountDueToday,
      balanceAtHotel,
      paymentMethod,
      hotel?.currency,
    ]
  );

  const markCheckoutConfirmed = useCallback(
    (apiResult) => {
      const bookingCode = apiResult?.booking_code || apiResult?.bookingCode;
      const bookingId = apiResult?.booking_id || apiResult?.bookingId;
      if (apiResult?.guest_id && hotelId) {
        try {
          localStorage.setItem(getGuestStorageKey(hotelId), String(apiResult.guest_id));
        } catch (_) {}
      }
      setCheckoutResult((prev) => {
        if (prev?.confirmationStatus === 'confirmed') return prev;
        return {
          ...(prev || buildPendingCheckoutResult(apiResult?.razorpay_order_id)),
          variant: 'success',
          confirmationStatus: 'confirmed',
          title: 'Booking confirmed',
          message: apiResult?.message || 'Booking confirmed after successful payment.',
          booking_code: bookingCode || prev?.booking_code,
          booking_id: bookingId || prev?.booking_id,
          razorpay_order_id: apiResult?.razorpay_order_id || prev?.razorpay_order_id,
        };
      });
    },
    [hotelId, buildPendingCheckoutResult]
  );

  useEffect(() => {
    if (checkoutResult?.confirmationStatus !== 'pending') return undefined;
    const orderId = checkoutResult?.razorpay_order_id;
    if (!orderId || !hotelId) return undefined;

    let cancelled = false;
    let timeoutId = null;
    let attempts = 0;
    const maxAttempts = 45;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const status = await getNexaStayPaymentStatus(hotelId, orderId);
        if (cancelled) return;
        if (status?.status === 'confirmed' && (status.booking_code || status.booking_id)) {
          markCheckoutConfirmed(status);
          return;
        }
        if (status?.status === 'expired') {
          setCheckoutResult({
            variant: 'error',
            title: 'Booking confirmation timed out',
            message:
              status.message ||
              'Payment session expired. Please contact the hotel with your payment reference.',
          });
          return;
        }
      } catch (_) {
        // Keep polling on transient network/API errors.
      }
      if (!cancelled && attempts < maxAttempts) {
        timeoutId = setTimeout(poll, 2000);
      } else if (!cancelled) {
        setCheckoutResult((prev) => {
          if (prev?.confirmationStatus !== 'pending') return prev;
          return {
            variant: 'error',
            title: 'Confirmation is taking longer',
            message:
              'Your payment was received but confirmation is taking longer than expected. Please check My Bookings shortly or contact the hotel.',
          };
        });
      }
    };

    timeoutId = setTimeout(poll, 1500);
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    checkoutResult?.confirmationStatus,
    checkoutResult?.razorpay_order_id,
    hotelId,
    markCheckoutConfirmed,
  ]);

  const buildBookingPayload = () => {
    const rawGuestDigits = digitsOnlyPhone(guestPhone);
    const phoneForApi = isIndiaDialCode(guestPhoneCountryCode)
      ? normalizeIndiaMobileDigits(rawGuestDigits)
      : rawGuestDigits;
    const guestPayload = {
      name: guestName.trim(),
      phone: phoneForApi,
      country_code: guestPhoneCountryCode || '+91',
      email: guestEmail.trim(),
    };
    if (bookingCustomerType === 'corporate') {
      guestPayload.company_name = companyName.trim();
      guestPayload.company_tax_id_type = companyTaxIdType;
      guestPayload.company_tax_id_number = companyTaxIdNumber.trim();
      const cp = companyPhone.replace(/\D/g, '');
      if (cp) guestPayload.company_phone = cp;
      const ce = companyEmail.trim();
      if (ce) guestPayload.company_email = ce;
      const ca = companyAddress.trim();
      if (ca) guestPayload.company_address = ca;
      const ccity = companyCity.trim();
      if (ccity) guestPayload.company_city = ccity;
      const cst = companyState.trim();
      if (cst) guestPayload.company_state = cst;
      const ccountry = companyCountry.trim();
      if (ccountry) guestPayload.company_country = ccountry;
      const czip = companyPincode.trim();
      if (czip) guestPayload.company_pincode = czip;
    }

    const payload = {
      check_in: checkIn,
      check_out: checkOut,
      rate_plan_id: ratePlanId || null,
      booking_type: bookingCustomerType,
      guest: guestPayload,
      rooms: roomDetails.map((rd) => ({
        roomTypeId: rd.rt.id,
        ratePlanId: rd.ratePlanId ?? null,
        adults: rd.adults ?? (rd.rt.base_adults != null ? Number(rd.rt.base_adults) : 1),
        children: rd.children ?? (rd.rt.base_children != null ? Number(rd.rt.base_children) : 0),
        infants: rd.infants ?? 0,
        pricePerNight: Math.round((rd.price / nights) * 100) / 100,
      })),
      payment_method: paymentMethod,
    };
    if (couponApplied && couponDiscountPercent > 0) {
      payload.couponCode = String(coupon || '').trim().toUpperCase();
    }
    if (!submitIdempotencyKeyRef.current) {
      submitIdempotencyKeyRef.current = generateIdempotencyKey();
    }
    payload.idempotencyKey = submitIdempotencyKeyRef.current;
    const gn = guestNote.trim();
    if (gn) payload.guest_note = gn;
    if (expectedArrivalTime && expectedArrivalTime.isValid?.()) {
      payload.expected_arrival_time = expectedArrivalTime.format('HH:mm');
    }
    return payload;
  };

  const openRazorpayCheckout = async (payload, callbacks = {}) => {
    const { onPaymentSuccess, onVerifyComplete, onVerifyError } = callbacks;
    const order = await initiateNexaStayPayment(hotelId, payload);
    const sdkLoaded = await loadRazorpayScript();
    if (!sdkLoaded || !window.Razorpay) {
      throw new Error('Unable to load payment checkout. Please check your internet and try again.');
    }

    const keyId = order.key_id;
    if (!keyId || !order.order_id) {
      throw new Error('Payment gateway is not configured. Please choose pay at hotel or contact the hotel.');
    }

    const { image: razorpayLogo, theme: razorpayTheme } = buildRazorpayBranding({ theme, hotel });

    return new Promise((resolve, reject) => {
      const razorpay = new window.Razorpay({
        key: keyId,
        amount: order.amount_minor,
        currency: order.currency || hotel?.currency || 'INR',
        name: hotel?.name || 'NexaStay',
        description: paymentMethod === 'full_pay' ? 'Full booking payment' : 'Partial booking payment',
        order_id: order.order_id,
        ...(razorpayLogo ? { image: razorpayLogo } : {}),
        theme: razorpayTheme,
        prefill: {
          name: (payload?.guest?.name || guestName).trim(),
          email: (payload?.guest?.email || guestEmail).trim(),
          contact: buildRazorpayContactPrefill(
            payload?.guest?.phone || guestPhone,
            payload?.guest?.country_code || guestPhoneCountryCode
          ),
        },
        handler: (response) => {
          const razorpayOrderId = response?.razorpay_order_id || order.order_id;
          const verifyPayload = {
            razorpay_order_id: razorpayOrderId,
            razorpay_payment_id: response?.razorpay_payment_id || '',
            razorpay_signature: response?.razorpay_signature || '',
          };

          onPaymentSuccess?.({ razorpay_order_id: razorpayOrderId, order });
          resolve({ pending: true, razorpay_order_id: razorpayOrderId });

          verifyNexaStayPayment(hotelId, verifyPayload)
            .then((result) => {
              onVerifyComplete?.(result);
            })
            .catch((verifyErr) => {
              onVerifyError?.(verifyErr, { razorpay_order_id: razorpayOrderId });
            });
        },
        modal: {
          ondismiss: () => {
            reject(Object.assign(new Error('Payment was cancelled.'), { code: 'PAYMENT_CANCELLED' }));
          },
        },
      });
      razorpay.on('payment.failed', (response) => {
        const reason =
          response?.error?.description ||
          response?.error?.reason ||
          'Payment failed. Please try again or choose another payment option.';
        reject(new Error(reason));
      });
      razorpay.open();
    });
  };

  const corporateFormOk =
    bookingCustomerType !== 'corporate' ||
    (String(companyName).trim().length > 0 && String(companyTaxIdNumber).trim().length > 0);

  /**
   * Do not require check_in/check_out here — they may be missing briefly until sessionStorage restore
   * or after OAuth; submit handler shows a clear error. Guest/room validation runs on click.
   */
  const canAttemptCheckout = Boolean(hotelId && hotel && termsAccepted && corporateFormOk);

  const updateRoomOccupancy = useCallback((roomIndex, field, delta) => {
    setEditableRoomOccupancies((prev) => {
      const next = prev.map((r, i) => ({ ...r, roomIndex: r.roomIndex ?? i }));
      const item = next[roomIndex];
      if (!item) return prev;
      const rt = roomTypesMap[item.roomTypeId];
      const maxGuests = rt?.max_guests ?? rt?.capacity ?? 10;
      const limits = { adults: [1, 9], children: [0, 9], infants: [0, 4] };
      const [min, max] = limits[field] ?? [0, 9];
      const baseAdultsRt = rt?.base_adults != null ? Number(rt.base_adults) : 1;
      const baseChildrenRt = rt?.base_children != null ? Number(rt.base_children) : 0;
      const cur = item[field] ?? (field === 'adults' ? baseAdultsRt : field === 'children' ? baseChildrenRt : 0);
      const nextVal = Math.min(max, Math.max(min, cur + delta));
      const otherAdults = field === 'adults' ? nextVal : (item.adults ?? baseAdultsRt);
      const otherChildren = field === 'children' ? nextVal : (item.children ?? baseChildrenRt);
      const otherInfants = field === 'infants' ? nextVal : (item.infants ?? 0);
      const totalGuests = otherAdults + otherChildren + otherInfants;
      if (totalGuests > maxGuests) return prev;
      return next.map((r, i) =>
        i === roomIndex ? { ...r, [field]: nextVal } : r
      );
    });
  }, [roomTypesMap]);

  const handleCouponInputChange = useCallback((event) => {
    const nextValue = event.target.value;
    setCoupon(nextValue);
    setCouponMessage('');
    if (!nextValue.trim() || couponApplied) {
      setCouponApplied(false);
      setCouponDiscountPercent(0);
    }
  }, [couponApplied]);

  const handleApplyCoupon = useCallback(async () => {
    const normalizedCode = String(coupon || '').trim().toUpperCase();
    if (!normalizedCode) {
      setCouponApplied(false);
      setCouponDiscountPercent(0);
      setCouponMessage('Enter a coupon code to apply.');
      return;
    }

    if (!hotelId) {
      setCouponApplied(false);
      setCouponDiscountPercent(0);
      setCouponMessage('Unable to validate coupon right now. Please refresh and try again.');
      return;
    }

    setCouponApplying(true);
    setCouponMessage('');
    try {
      const response = await validateNexaStayCoupon(hotelId, normalizedCode);
      const validatedPercent = Math.max(0, Math.min(100, Number(response?.discountPercent) || 0));
      if (!response?.couponApplied || validatedPercent <= 0) {
        throw new Error(response?.message || 'Invalid or expired coupon code.');
      }
      setCoupon(normalizedCode);
      setCouponApplied(true);
      setCouponDiscountPercent(validatedPercent);
      setCouponMessage(response?.message || `Coupon applied (${validatedPercent}% off).`);
    } catch (err) {
      const statusCode = Number(err?.response?.status) || 0;
      const serverMessage = err?.response?.data?.message || err?.response?.data?.detail || '';
      const message = statusCode >= 400 && statusCode < 500
        ? 'Coupon is not valid.'
        : (serverMessage || 'Coupon is not valid.');
      setCouponApplied(false);
      setCouponDiscountPercent(0);
      setCouponMessage(String(message));
    } finally {
      setCouponApplying(false);
    }
  }, [coupon, hotelId]);

  const handleModify = () => {
    submitIdempotencyKeyRef.current = null;
    const totalRooms = editableRoomOccupancies.length;
    const roomsJson = JSON.stringify(
      editableRoomOccupancies.map((r, i) => {
        const rt = roomTypesMap[r.roomTypeId];
        const baseA = rt?.base_adults != null ? Number(rt.base_adults) : 1;
        const baseC = rt?.base_children != null ? Number(rt.base_children) : 0;
        return {
          roomTypeId: r.roomTypeId,
          roomIndex: r.roomIndex ?? i,
          ratePlanId: r.ratePlanId ?? undefined,
          adults: r.adults ?? baseA,
          children: r.children ?? baseC,
          infants: r.infants ?? 0,
        };
      })
    );
    const params = new URLSearchParams({
      check_in: checkIn,
      check_out: checkOut,
      rooms: String(totalRooms),
      adults: String(editableRoomOccupancies.reduce((s, r) => s + (r.adults || 0), 0)),
      children: String(editableRoomOccupancies.reduce((s, r) => s + (r.children || 0), 0)),
      rooms_json: roomsJson,
    });
    if (ratePlanId) params.set('rate_plan_id', ratePlanId);
    editableRoomOccupancies.forEach((r) => params.append('room_type', r.roomTypeId));
    navigate(`${nexastayPath(basePath, hotelId)}?${params.toString()}`);
  };

  const handleConfirmBooking = async () => {
    if (bookingSubmitting) {
      return;
    }

    setGuestNameTouched(true);
    setGuestPhoneTouched(true);
    setGuestEmailTouched(true);
    const nameErr = validateName(guestName);
    const phoneErr = validatePhone(guestPhone, guestPhoneCountryCode);
    const emailErr = validateEmail(guestEmail);
    setGuestNameError(nameErr);
    setGuestPhoneError(phoneErr);
    setGuestEmailError(emailErr);

    if (!hotelId || !checkIn || !checkOut) {
      setBookingError('Missing booking dates or hotel. Please refresh the page.');
      return;
    }
    if (!termsAccepted) {
      setBookingError('Please accept the terms and conditions to continue.');
      return;
    }
    if (!corporateFormOk) {
      setBookingError('Please complete corporate details or switch to Individual.');
      return;
    }

    const noRooms = !(roomDetails?.length);
    if (noRooms) {
      setBookingError(
        'No rooms are available for this booking. Use “Modify” to re-select rooms, or go back to the hotel page and try again.'
      );
    } else {
      setBookingError('');
    }

    if (nameErr || phoneErr || emailErr || noRooms) {
      return;
    }

    setBookingSubmitting(true);
    setBookingError('');
    try {
      const payload = buildBookingPayload();
      if (paymentMethod === 'pay_at_hotel') {
        const result = await createBooking(hotelId, payload);
        const bookingCode = result?.booking_code || result?.bookingCode;
        const bookingId = result?.booking_id || result?.bookingId;
        if (!bookingCode && !bookingId) {
          throw new Error('Booking could not be created. Please try again or contact the hotel.');
        }
        if (result?.guest_id && hotelId) {
          try {
            localStorage.setItem(getGuestStorageKey(hotelId), String(result.guest_id));
          } catch (_) {}
        }
        setBookingError('');
        setCheckoutResult({
          variant: 'success',
          confirmationStatus: 'confirmed',
          title: 'Booking confirmed',
          message: result?.message || 'Your reservation has been confirmed successfully.',
          booking_code: bookingCode,
          booking_id: bookingId,
          check_in: checkIn,
          check_out: checkOut,
          total_amount: payableAmount,
          amount_paid: 0,
          balance_at_hotel: payableAmount,
          payment_method: paymentMethod,
          currency: hotel?.currency || 'INR',
        });
      } else {
        await openRazorpayCheckout(payload, {
          onPaymentSuccess: ({ razorpay_order_id: razorpayOrderId }) => {
            setCheckoutResult(buildPendingCheckoutResult(razorpayOrderId));
          },
          onVerifyComplete: (result) => {
            if (
              result?.status === 'confirmed' ||
              result?.booking_code ||
              result?.booking_id ||
              result?.bookingCode ||
              result?.bookingId
            ) {
              markCheckoutConfirmed(result);
            }
          },
          onVerifyError: () => {
            // Payment succeeded; polling + verify retry via status API will confirm the booking.
          },
        });
      }
    } catch (err) {
      const msg = extractCheckoutError(err);
      setBookingError(msg);
      setCheckoutResult({
        variant: err?.code === 'PAYMENT_CANCELLED' ? 'warning' : 'error',
        title: err?.code === 'PAYMENT_CANCELLED' ? 'Payment cancelled' : 'Could not complete booking',
        message: msg,
      });
    } finally {
      setBookingSubmitting(false);
    }
  };

  const confirmButtonLabel = useMemo(() => {
    if (bookingSubmitting) {
      return paymentMethod === 'pay_at_hotel' ? 'Creating booking…' : 'Opening secure payment…';
    }
    if (paymentMethod === 'pay_at_hotel') {
      return `Confirm booking · ${symbol}${payableAmount.toFixed(2)} at hotel`;
    }
    if (paymentMethod === 'full_pay') {
      return `Pay ${symbol}${payableAmount.toFixed(2)} & confirm`;
    }
    return `Pay ${symbol}${amountDueToday.toFixed(2)} now & confirm`;
  }, [bookingSubmitting, paymentMethod, symbol, payableAmount, amountDueToday]);

  const dueTodayDisplay = useMemo(() => {
    if (paymentMethod === 'pay_at_hotel') {
      return { label: 'Due at check-in', amount: payableAmount };
    }
    return { label: 'Due today', amount: amountDueToday };
  }, [paymentMethod, payableAmount, amountDueToday]);

  if (!hotelId) {
    return (
      <Box sx={{ ...mainBgSx, p: 3, color: theme.textPrimary }}>
        <Typography>Missing hotel ID.</Typography>
      </Box>
    );
  }

  if (loading && !hotel) {
    return (
      <Box sx={{ ...mainBgSx, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ color: theme.accent }}>Loading…</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...mainBgSx,
        color: theme.textPrimary,
        pt: { xs: 1, sm: 2 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -120,
          right: -80,
          width: 420,
          height: 420,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.accent, 0.14)} 0%, transparent 68%)`,
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 80,
          left: -100,
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.accent, 0.08)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Box sx={{ pb: 4, px: { xs: 1.5, sm: 2 }, scrollMarginTop: { xs: 72, sm: 88 }, position: 'relative', zIndex: 1 }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, minWidth: 0, alignItems: 'flex-start' }}>
          {/* Main content */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, minWidth: 0 }}>
            {/* Left column: Checkout Details */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CheckoutSectionHeader
                icon={ReceiptLongIcon}
                title="Your stay"
                subtitle={
                  availability?.rate_plan?.name
                    ? `${formatDisplayDate(checkIn)} → ${formatDisplayDate(checkOut)} · ${availability.rate_plan.name}`
                    : `${formatDisplayDate(checkIn)} → ${formatDisplayDate(checkOut)}`
                }
                theme={theme}
                action={
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon sx={{ fontSize: 18 }} />}
                    onClick={handleModify}
                    sx={{
                      color: theme.accent,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      borderColor: alpha(theme.accent, 0.45),
                      bgcolor: alpha(theme.accent, 0.08),
                      '&:hover': { borderColor: theme.accent, bgcolor: alpha(theme.accent, 0.16) },
                    }}
                  >
                    Modify
                  </Button>
                }
              />

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 3,
                  ...card3DSx,
                  background: `linear-gradient(180deg, ${alpha(theme.accent, 0.06)} 0%, transparent 48px)`,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <CalendarMonthIcon sx={{ color: theme.accent, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: theme.textPrimary, fontWeight: 500 }}>
                      Check In {formatDisplayDate(checkIn)}
                    </Typography>
                  </Stack>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: `${theme.accent}80` }} />
                    <Box sx={{ width: 24, height: 1, bgcolor: theme.border, borderStyle: 'dashed' }} />
                    <Box sx={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${theme.accent}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" sx={{ color: theme.accent, fontWeight: 700 }}>{nights}</Typography>
                    </Box>
                    <Box sx={{ width: 24, height: 1, bgcolor: theme.border, borderStyle: 'dashed' }} />
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: `${theme.accent}80` }} />
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <CalendarMonthIcon sx={{ color: theme.accent, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: theme.textPrimary, fontWeight: 500 }}>
                      Check Out {formatDisplayDate(checkOut)}
                    </Typography>
                  </Stack>
                </Stack>

                {roomDetails.map(({ rt, adults, children, infants, price, extraAdultAmount, extraChildAmount, extraAdultsCount, extraChildrenCount, planName }, idx) => (
                  <Card
                    key={idx}
                    elevation={0}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      minHeight: { xs: 'auto', sm: 160 },
                      mb: idx < roomDetails.length - 1 ? 2 : 0,
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: `${theme.accent}18`,
                      border: `1px solid ${theme.accent}26`,
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 2px 8px rgba(0,0,0,0.2)',
                      transition: 'all 0.25s ease',
                      '&:hover': { boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 4px 16px rgba(0,0,0,0.3)' },
                    }}
                  >
                    <Box
                      sx={{
                        flexShrink: 0,
                        width: { xs: '100%', sm: 220 },
                        height: { xs: 160, sm: 160 },
                        minHeight: { xs: 160, sm: 'auto' },
                        overflow: 'hidden',
                        borderRadius: { xs: '8px 8px 0 0', sm: '8px 0 0 8px' },
                      }}
                    >
                      <Box
                        component="img"
                        src={rt.image_url || hotel?.image_url || 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=280&q=80'}
                        alt={rt.name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center',
                          display: 'block',
                          verticalAlign: 'middle',
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, p: { xs: 1.5, sm: 2 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ color: theme.textPrimary, fontWeight: 600 }}>
                          {rt.name}
                        </Typography>
                        {planName && (
                          <Chip
                            icon={<RestaurantIcon sx={{ fontSize: 16 }} />}
                            label={planName}
                            size="small"
                            sx={{
                              height: 28,
                              fontWeight: 600,
                              bgcolor: `${theme.accent}22`,
                              color: theme.accent,
                              border: `1px solid ${theme.accent}55`,
                              boxShadow: `0 2px 8px ${theme.accent}25, inset 0 1px 0 rgba(255,255,255,0.12)`,
                              '& .MuiChip-icon': { color: theme.accent },
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: `${theme.accent}33`,
                                borderColor: theme.accent,
                                boxShadow: `0 3px 12px ${theme.accent}40`,
                              },
                            }}
                          />
                        )}
                      </Stack>
                      {rt.max_guests != null && (
                        <Typography variant="caption" sx={{ color: theme.textMuted, mb: 0.5 }}>Max {rt.max_guests} guest{rt.max_guests !== 1 ? 's' : ''} per room</Typography>
                      )}
                      <Stack direction="row" flexWrap="wrap" spacing={2} sx={{ mb: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <PersonIcon sx={{ color: theme.textSecondary, fontSize: 18 }} />
                          <Typography variant="caption" sx={{ color: theme.textSecondary }}>Adults</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', border: `1px solid ${theme.border}`, borderRadius: 1, bgcolor: theme.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)' }}>
                            <IconButton size="small" onClick={() => updateRoomOccupancy(idx, 'adults', -1)} disabled={adults <= 1} sx={{ color: theme.accent, width: 32, height: 32, '&.Mui-disabled': { color: theme.textMuted } }}><RemoveIcon sx={{ fontSize: 16 }} /></IconButton>
                            <Typography variant="body2" sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600, color: theme.textPrimary }}>{adults}</Typography>
                            <IconButton size="small" onClick={() => updateRoomOccupancy(idx, 'adults', 1)} disabled={adults >= 9 || (adults + children + infants) >= (rt.max_guests ?? 10)} sx={{ color: theme.accent, width: 32, height: 32, '&.Mui-disabled': { color: theme.textMuted } }}><AddIcon sx={{ fontSize: 16 }} /></IconButton>
                          </Box>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <ChildCareIcon sx={{ color: theme.textSecondary, fontSize: 18 }} />
                          <Typography variant="caption" sx={{ color: theme.textSecondary }}>Children</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', border: `1px solid ${theme.border}`, borderRadius: 1, bgcolor: theme.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)' }}>
                            <IconButton size="small" onClick={() => updateRoomOccupancy(idx, 'children', -1)} disabled={children <= 0} sx={{ color: theme.accent, width: 32, height: 32, '&.Mui-disabled': { color: theme.textMuted } }}><RemoveIcon sx={{ fontSize: 16 }} /></IconButton>
                            <Typography variant="body2" sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600, color: theme.textPrimary }}>{children}</Typography>
                            <IconButton size="small" onClick={() => updateRoomOccupancy(idx, 'children', 1)} disabled={children >= 9 || (adults + children + infants) >= (rt.max_guests ?? 10)} sx={{ color: theme.accent, width: 32, height: 32, '&.Mui-disabled': { color: theme.textMuted } }}><AddIcon sx={{ fontSize: 16 }} /></IconButton>
                          </Box>
                        </Stack>
                      </Stack>
                      <Typography variant="body2" sx={{ color: theme.accent, fontWeight: 600 }}>
                        {symbol}{(price * (1 + taxRate)).toFixed(2)}{gstIncluded && taxPercentageDisplay > 0 ? ' incl. GST' : ''}
                      </Typography>
                      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                        {extraAdultAmount > 0 && (
                          <Typography variant="caption" sx={{ color: theme.textSecondary }}>
                            Extra adult{extraAdultsCount !== 1 ? 's' : ''} ({extraAdultsCount}): {symbol}{extraAdultAmount.toFixed(2)}
                          </Typography>
                        )}
                        {extraChildAmount > 0 && (
                          <Typography variant="caption" sx={{ color: theme.textSecondary }}>
                            Extra child{extraChildrenCount !== 1 ? 'ren' : ''} ({extraChildrenCount}): {symbol}{extraChildAmount.toFixed(2)}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Card>
                ))}
              </Paper>

              <Paper elevation={0} sx={{ p: 2, mb: 2, ...card3DSx }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <LocalOfferOutlinedIcon sx={{ color: theme.accent, fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ color: theme.textPrimary, fontWeight: 700 }}>
                    Coupon code
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    fullWidth
                    label="Coupon"
                    value={coupon}
                    onChange={handleCouponInputChange}
                    variant="outlined"
                    size="medium"
                    sx={{ flex: 1, ...inputSx }}
                  />
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={handleApplyCoupon}
                    disabled={couponApplying}
                    sx={{
                      borderColor: theme.accent,
                      color: theme.accent,
                      '&:hover': { borderColor: theme.accent, bgcolor: `${theme.accent}1a` },
                    }}
                  >
                    {couponApplying ? 'Applying…' : 'Apply'}
                  </Button>
                </Stack>
                {couponMessage ? (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 1.25,
                      color: couponApplied ? theme.accent : 'error.main',
                      fontWeight: couponApplied ? 600 : 500,
                    }}
                  >
                    {couponMessage}
                  </Typography>
                ) : null}
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 0,
                  mb: 2,
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(theme.accent, 0.28)}`,
                  boxShadow: `0 8px 32px ${alpha(theme.accent, 0.12)}`,
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    background: `linear-gradient(90deg, ${alpha(theme.accent, 0.22)} 0%, ${alpha(theme.accent, 0.06)} 100%)`,
                    borderBottom: `1px solid ${alpha(theme.accent, 0.2)}`,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: theme.textPrimary, fontWeight: 700 }}>
                    Price summary
                  </Typography>
                </Box>
                <Stack spacing={1.25} sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: theme.textSecondary }}>Room total</Typography>
                    <Typography variant="body2" sx={{ color: theme.textPrimary, fontWeight: 600 }}>{symbol}{roomTotal.toFixed(2)}</Typography>
                  </Stack>
                  {discount > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ color: theme.accent }}>
                        Discount ({Math.max(0, Math.min(100, Number(couponDiscountPercent) || 0))}%)
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.accent, fontWeight: 700 }}>-{symbol}{discount.toFixed(2)}</Typography>
                    </Stack>
                  )}
                  {gstIncluded && taxPercentageDisplay > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ color: theme.textSecondary }}>GST ({taxPercentageDisplay}%)</Typography>
                      <Typography variant="body2" sx={{ color: theme.textPrimary, fontWeight: 600 }}>{symbol}{tax.toFixed(2)}</Typography>
                    </Stack>
                  )}
                  <Divider sx={{ borderColor: alpha(theme.accent, 0.15) }} />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" sx={{ color: theme.textPrimary, fontWeight: 700 }}>
                      Total
                    </Typography>
                    <Typography variant="h5" sx={{ color: theme.accent, fontWeight: 800, letterSpacing: '-0.02em' }}>
                      {symbol}{payableAmount.toFixed(2)}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Box>

            {/* Right column: Guest + Payment */}
            <Box sx={{ flex: 1, minWidth: 0, maxWidth: 560, position: { md: 'sticky' }, top: { md: 80 }, scrollMarginTop: 80 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(theme.accent, 0.3)}`,
                  boxShadow: `0 16px 48px ${alpha('#000', theme.mode === 'dark' ? 0.35 : 0.12)}, 0 0 0 1px ${alpha(theme.accent, 0.08)}`,
                  background: theme.mode === 'dark'
                    ? `linear-gradient(165deg, ${alpha(theme.accent, 0.1)} 0%, ${alpha(theme.paper, 0.98)} 120px)`
                    : `linear-gradient(165deg, ${alpha(theme.accent, 0.08)} 0%, ${theme.paper} 120px)`,
                }}
              >
                <CheckoutSectionHeader
                  icon={PersonOutlineOutlinedIcon}
                  title="Guest details"
                  subtitle="Who is checking in?"
                  theme={theme}
                />
                <Stack spacing={2.5}>
                  <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <FormLabel component="legend" sx={{ color: theme.textSecondary, fontSize: '0.875rem', mb: 0.5 }}>
                      Booking as
                    </FormLabel>
                    <RadioGroup
                      row
                      value={bookingCustomerType}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === 'individual') {
                          setBookingCustomerType('individual');
                          corporateDialogIntentRef.current = null;
                          setCorporateDialogOpen(false);
                          setCorporateFieldErrors(CORPORATE_FIELD_ERRORS_INITIAL);
                          return;
                        }
                        setBookingCustomerType('corporate');
                        corporateDialogIntentRef.current = 'new';
                        setCorporateFieldErrors(CORPORATE_FIELD_ERRORS_INITIAL);
                        setCorporateDialogOpen(true);
                      }}
                    >
                      <FormControlLabel
                        value="individual"
                        control={<Radio sx={{ color: theme.accent, '&.Mui-checked': { color: theme.accent } }} />}
                        label={<Typography sx={{ color: theme.textPrimary }}>Individual</Typography>}
                      />
                      <FormControlLabel
                        value="corporate"
                        control={<Radio sx={{ color: theme.accent, '&.Mui-checked': { color: theme.accent } }} />}
                        label={<Typography sx={{ color: theme.textPrimary }}>Corporate</Typography>}
                      />
                    </RadioGroup>
                  </FormControl>

                  {bookingCustomerType === 'corporate' && corporateFormOk && !corporateDialogOpen && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${theme.border}`,
                        bgcolor: theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                      }}
                    >
                      <Typography variant="body2" sx={{ color: theme.textPrimary, fontWeight: 500 }}>
                        Corporate · {companyName.trim()}
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          corporateDialogIntentRef.current = 'edit';
                          setCorporateFieldErrors(CORPORATE_FIELD_ERRORS_INITIAL);
                          setCorporateDialogOpen(true);
                        }}
                        sx={{ color: theme.accent, fontWeight: 600, flexShrink: 0 }}
                      >
                        Edit
                      </Button>
                    </Stack>
                  )}

                  <TextField
                  fullWidth
                  label="Name *"
                  variant="outlined"
                  size="medium"
                  value={guestName}
                  onChange={(e) => {
                    setGuestName(e.target.value);
                    if (guestNameTouched) setGuestNameError(validateName(e.target.value));
                  }}
                  onBlur={(e) => {
                    setGuestNameTouched(true);
                    setGuestNameError(validateName(e.target.value));
                  }}
                  error={!!guestNameError}
                  helperText={guestNameError}
                  sx={inputSx}
                />
                  {bookingCustomerType === 'individual' ? (
                    <Autocomplete
                      options={guestCountries}
                      getOptionLabel={(option) => option?.name || ''}
                      filterOptions={countryFilterOptions}
                      value={selectedGuestCountryObj}
                      isOptionEqualToValue={(opt, val) => opt?.code === val?.code}
                      onChange={(event, newValue) => {
                        const countryName = newValue?.name || '';
                        setGuestCountryTouched(true);
                        setGuestCountry(countryName);
                        applyDialCodeForCountry(newValue?.code || countryName);
                        if (guestPhoneTouched) {
                          setGuestPhoneError(
                            validatePhone(
                              guestPhone,
                              resolveDialCodeForCountryInput(newValue?.code || countryName, countryCodes, guestCountries) ||
                                guestPhoneCountryCode
                            )
                          );
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label="Country"
                          variant="outlined"
                          size="medium"
                          helperText="Phone country code follows the selected country"
                          sx={inputSx}
                        />
                      )}
                    />
                  ) : null}
                  <Grid container spacing={0} sx={{ width: '100%' }}>
                    <Grid item xs={12} sm={8} sx={{ pr: { xs: 0, sm: 1 } }}>
                      <TextField
                        fullWidth
                        label="Phone Number *"
                        variant="outlined"
                        size="medium"
                        value={guestPhone}
                        onChange={(e) => {
                          const dialCode = guestPhoneCountryCode || selectedPhoneCountryCode?.dialCode || '+91';
                          const next = normalizePhoneInputForCountry(e.target.value, dialCode);
                          setGuestPhone(next);
                          if (guestPhoneTouched) setGuestPhoneError(validatePhone(next, dialCode));
                        }}
                        onBlur={(e) => {
                          setGuestPhoneTouched(true);
                          const dialCode = guestPhoneCountryCode || selectedPhoneCountryCode?.dialCode || '+91';
                          const v = normalizePhoneInputForCountry(e.target.value, dialCode);
                          setGuestPhoneError(validatePhone(v, dialCode));
                        }}
                        error={!!guestPhoneError}
                        helperText={
                          guestPhoneError ||
                          `Enter ${formatPhoneLengthLabel(guestPhoneCountryCode || selectedPhoneCountryCode?.dialCode || '+91')}`
                        }
                        sx={{
                          ...inputSx,
                          '& .MuiFormHelperText-root': {
                            marginLeft: 0,
                            marginTop: 0.5,
                          },
                        }}
                        inputProps={{ inputMode: 'tel' }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box
                                ref={phoneCountryCodeAnchorRef}
                                onClick={() => setPhoneCountryCodeMenuOpen(true)}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  gap: 0.5,
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  '&:hover': { bgcolor: theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' },
                                }}
                              >
                                <Typography component="span" sx={{ fontSize: '1.2rem' }}>{selectedPhoneCountryCode?.flag || '🇮🇳'}</Typography>
                                <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.875rem', color: theme.textPrimary }}>{selectedPhoneCountryCode?.dialCode || '+91'}</Typography>
                                <Typography component="span" sx={{ fontSize: '0.75rem', color: theme.textSecondary }}>▼</Typography>
                              </Box>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Menu
                        anchorEl={phoneCountryCodeAnchorRef.current}
                        open={phoneCountryCodeMenuOpen}
                        onClose={() => { setPhoneCountryCodeMenuOpen(false); setPhoneCountryCodeSearch(''); }}
                        PaperProps={{ sx: { maxHeight: 400, width: 300 } }}
                      >
                        <Box sx={{ p: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Search country..."
                            value={phoneCountryCodeSearch}
                            onChange={(e) => setPhoneCountryCodeSearch(e.target.value)}
                            sx={{ mb: 1 }}
                          />
                        </Box>
                        {countryCodes
                          .filter((c) => {
                            if (!phoneCountryCodeSearch) return true;
                            const q = phoneCountryCodeSearch.toLowerCase();
                            return c.name.toLowerCase().includes(q) || c.dialCode.includes(q) || c.code.toLowerCase().includes(q);
                          })
                          .map((c) => (
                            <MenuItem
                              key={c.code}
                              onClick={() => {
                                setGuestPhoneCountryCodeTouched(true);
                                setGuestPhoneCountryCode(c.dialCode);
                                setGuestCountryTouched(true);
                                setGuestCountry(c.name);
                                setPhoneCountryCodeMenuOpen(false);
                                setPhoneCountryCodeSearch('');
                                const normalizedPhone = normalizePhoneInputForCountry(guestPhone, c.dialCode);
                                setGuestPhone(normalizedPhone);
                                if (guestPhoneTouched) {
                                  setGuestPhoneError(validatePhone(normalizedPhone, c.dialCode));
                                }
                              }}
                              selected={selectedPhoneCountryCode?.dialCode === c.dialCode}
                            >
                              <ListItemIcon>
                                <Typography sx={{ fontSize: '1.2rem' }}>{c.flag}</Typography>
                              </ListItemIcon>
                              <ListItemText primary={c.dialCode} secondary={c.name} primaryTypographyProps={{ fontWeight: 500 }} />
                            </MenuItem>
                          ))}
                      </Menu>
                    </Grid>
                    <Grid item xs={12} sm={4} sx={{ pl: { xs: 0, sm: 1 }, pt: { xs: 2.5, sm: 0 } }}>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <TimePicker
                          label="Expected arrival"
                          value={expectedArrivalTime}
                          onChange={(v) => setExpectedArrivalTime(v || null)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: 'outlined',
                              size: 'medium',
                              sx: inputSx,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                  <TextField
                    fullWidth
                    label="Email ID *"
                    type="email"
                    variant="outlined"
                    size="medium"
                    value={guestEmail}
                    onChange={(e) => {
                      setGuestEmail(e.target.value);
                      if (guestEmailTouched) setGuestEmailError(validateEmail(e.target.value));
                    }}
                    onBlur={(e) => {
                      setGuestEmailTouched(true);
                      setGuestEmailError(validateEmail(e.target.value));
                    }}
                    error={!!guestEmailError}
                    helperText={guestEmailError}
                    sx={inputSx}
                    placeholder="you@example.com"
                  />
                  <TextField
                    fullWidth
                    label="Note"
                    multiline
                    rows={3}
                    variant="outlined"
                    size="medium"
                    value={guestNote}
                    onChange={(e) => setGuestNote(e.target.value)}
                    sx={inputSx}
                  />
                </Stack>

                <Divider sx={{ my: 2.5, borderColor: alpha(theme.accent, 0.12) }} />

                <CheckoutSectionHeader
                  icon={PaymentsOutlinedIcon}
                  title="Payment method"
                  subtitle="Choose how you'd like to pay"
                  theme={theme}
                />
                <Box
                  role="radiogroup"
                  aria-label="Payment method"
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1.25,
                    mb: 2,
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                  }}
                >
                  {availablePaymentMethods.includes('pay_at_hotel') && (
                    <CheckoutPaymentOption
                      selected={paymentMethod === 'pay_at_hotel'}
                      onSelect={() => setPaymentMethod('pay_at_hotel')}
                      icon={HotelOutlinedIcon}
                      title="Pay at hotel"
                      amountLabel={`${symbol}${payableAmount.toFixed(2)} at check-in`}
                      hint="No online payment now"
                      badge="Cash"
                      theme={theme}
                    />
                  )}
                  {availablePaymentMethods.includes('partial_pay') && (
                    <CheckoutPaymentOption
                      selected={paymentMethod === 'partial_pay'}
                      onSelect={() => setPaymentMethod('partial_pay')}
                      icon={PieChartOutlineIcon}
                      title={`Partial pay (${partialPayPercent}%)`}
                      amountLabel={`${symbol}${computeAmountDueToday(payableAmount, 'partial_pay', partialPayPercent).toFixed(2)} now`}
                      hint={`${symbol}${(payableAmount - computeAmountDueToday(payableAmount, 'partial_pay', partialPayPercent)).toFixed(2)} balance at hotel`}
                      theme={theme}
                    />
                  )}
                  {availablePaymentMethods.includes('full_pay') && (
                    <CheckoutPaymentOption
                      selected={paymentMethod === 'full_pay'}
                      onSelect={() => setPaymentMethod('full_pay')}
                      icon={CreditCardOutlinedIcon}
                      title="Full payment"
                      amountLabel={`${symbol}${payableAmount.toFixed(2)} now`}
                      hint="Pay online to confirm booking"
                      theme={theme}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    p: 1.5,
                    mb: 2,
                    borderRadius: 2,
                    border: `1px dashed ${alpha(theme.accent, 0.35)}`,
                    bgcolor: alpha(theme.accent, 0.08),
                  }}
                >
                  <Typography variant="body2" sx={{ color: theme.textSecondary, fontWeight: 600 }}>
                    {dueTodayDisplay.label}
                  </Typography>
                  <Typography variant="h6" sx={{ color: theme.accent, fontWeight: 800 }}>
                    {symbol}{dueTodayDisplay.amount.toFixed(2)}
                  </Typography>
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      sx={{ color: theme.accent, '&.Mui-checked': { color: theme.accent } }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: theme.textPrimary }}>
                      I accept the Hotel's{' '}
                      <Box component="span" sx={{ color: theme.accent, cursor: 'pointer' }}>Terms</Box>
                      ,{' '}
                      <Box component="span" sx={{ color: theme.accent, cursor: 'pointer' }}>Privacy</Box>
                      {' & '}
                      <Box component="span" sx={{ color: theme.accent, cursor: 'pointer' }}>Cancellation</Box>
                      {' policies.'}
                    </Typography>
                  }
                  sx={{ mb: 1.5, alignItems: 'flex-start' }}
                />

                {bookingError ? (
                  <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>
                    {bookingError}
                  </Alert>
                ) : null}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={bookingSubmitting || !canAttemptCheckout}
                  onClick={handleConfirmBooking}
                  startIcon={paymentMethod !== 'pay_at_hotel' ? <LockOutlinedIcon /> : undefined}
                  sx={{
                    py: 1.75,
                    fontSize: '1rem',
                    fontWeight: 800,
                    borderRadius: 2.5,
                    textTransform: 'none',
                    letterSpacing: 0.2,
                    bgcolor: canAttemptCheckout ? theme.accent : alpha(theme.textMuted, 0.15),
                    color: canAttemptCheckout ? (theme.mode === 'dark' ? '#121212' : '#000') : theme.textMuted,
                    boxShadow: canAttemptCheckout
                      ? `0 10px 28px ${alpha(theme.accent, 0.45)}`
                      : 'none',
                    transition: 'all 0.25s ease',
                    '&:hover': canAttemptCheckout
                      ? { transform: 'translateY(-2px)', boxShadow: `0 14px 36px ${alpha(theme.accent, 0.5)}` }
                      : {},
                  }}
                >
                  {confirmButtonLabel}
                </Button>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={corporateDialogOpen}
        onClose={(e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            dismissCorporateDialog(true);
          }
        }}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${theme.accent}40`,
            bgcolor: theme.paper,
            maxHeight: 'min(90vh, 720px)',
          },
        }}
      >
        <DialogTitle sx={{ color: theme.textPrimary, fontWeight: 700, pb: 1 }}>
          Company / billing details
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: theme.border, pt: 2, px: 2, pb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Company name"
                variant="outlined"
                size="medium"
                value={companyName}
                onChange={(e) => {
                  setCompanyName(e.target.value);
                  if (corporateFieldErrors.companyName) {
                    setCorporateFieldErrors((p) => ({ ...p, companyName: '' }));
                  }
                }}
                error={!!corporateFieldErrors.companyName}
                helperText={corporateFieldErrors.companyName || undefined}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    label="Tax ID type"
                    variant="outlined"
                    size="medium"
                    value={companyTaxIdType}
                    onChange={(e) => setCompanyTaxIdType(e.target.value)}
                    sx={inputSx}
                  >
                    {CORPORATE_TAX_ID_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    required
                    label="Tax ID number"
                    variant="outlined"
                    size="medium"
                    value={companyTaxIdNumber}
                    onChange={(e) => {
                      setCompanyTaxIdNumber(e.target.value);
                      if (corporateFieldErrors.companyTaxIdNumber) {
                        setCorporateFieldErrors((p) => ({ ...p, companyTaxIdNumber: '' }));
                      }
                    }}
                    error={!!corporateFieldErrors.companyTaxIdNumber}
                    helperText={corporateFieldErrors.companyTaxIdNumber || undefined}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company phone"
                variant="outlined"
                size="medium"
                value={companyPhone}
                onChange={(e) => {
                  setCompanyPhone(e.target.value.replace(/\D/g, '').slice(0, 15));
                  if (corporateFieldErrors.companyPhone) {
                    setCorporateFieldErrors((p) => ({ ...p, companyPhone: '' }));
                  }
                }}
                error={!!corporateFieldErrors.companyPhone}
                helperText={corporateFieldErrors.companyPhone || undefined}
                sx={inputSx}
                inputProps={{ inputMode: 'tel' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company email"
                type="email"
                variant="outlined"
                size="medium"
                value={companyEmail}
                onChange={(e) => {
                  setCompanyEmail(e.target.value);
                  if (corporateFieldErrors.companyEmail) {
                    setCorporateFieldErrors((p) => ({ ...p, companyEmail: '' }));
                  }
                }}
                error={!!corporateFieldErrors.companyEmail}
                helperText={corporateFieldErrors.companyEmail || undefined}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Billing address"
                variant="outlined"
                size="medium"
                value={companyAddress}
                onChange={(e) => {
                  setCompanyAddress(e.target.value);
                  if (corporateFieldErrors.companyAddress) {
                    setCorporateFieldErrors((p) => ({ ...p, companyAddress: '' }));
                  }
                }}
                error={!!corporateFieldErrors.companyAddress}
                helperText={corporateFieldErrors.companyAddress || undefined}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={companyCountries}
                getOptionLabel={(option) => option?.name || ''}
                filterOptions={countryFilterOptions}
                value={selectedCompanyCountryObj}
                isOptionEqualToValue={(opt, val) => opt?.code === val?.code}
                componentsProps={{ popper: { sx: { zIndex: 1400 } } }}
                onChange={(event, newValue) => {
                  const countryName = newValue?.name || '';
                  setCompanyCountry(countryName);
                  setCompanyState('');
                  setCompanyCity('');
                  setCorporateFieldErrors((p) => ({ ...p, country: '', state: '', city: '' }));
                  applyDialCodeForCountry(newValue?.code || countryName);
                  if (!guestCountryTouched) {
                    setGuestCountry(countryName);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Country"
                    variant="outlined"
                    size="medium"
                    error={!!corporateFieldErrors.country}
                    helperText={corporateFieldErrors.country || undefined}
                    sx={inputSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={companyStates}
                getOptionLabel={(option) => option?.name || ''}
                filterOptions={stateFilterOptions}
                value={selectedCompanyStateObj}
                isOptionEqualToValue={(opt, val) => opt?.name === val?.name}
                componentsProps={{ popper: { sx: { zIndex: 1400 } } }}
                onChange={(event, newValue) => {
                  const stateName = newValue?.name || '';
                  setCompanyState(stateName);
                  setCompanyCity('');
                  setCorporateFieldErrors((p) => ({ ...p, state: '', city: '' }));
                }}
                disabled={!selectedCompanyCountryObj}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="State"
                    variant="outlined"
                    size="medium"
                    error={!!corporateFieldErrors.state}
                    helperText={corporateFieldErrors.state || undefined}
                    sx={inputSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={companyCities}
                getOptionLabel={(option) => option || ''}
                filterOptions={cityFilterOptions}
                value={companyCities.find((city) => city === companyCity) || null}
                isOptionEqualToValue={(opt, val) => opt === val}
                componentsProps={{ popper: { sx: { zIndex: 1400 } } }}
                onChange={(event, newValue) => {
                  setCompanyCity(newValue || '');
                  setCorporateFieldErrors((p) => ({ ...p, city: '' }));
                }}
                disabled={!selectedCompanyStateObj}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="City"
                    variant="outlined"
                    size="medium"
                    error={!!corporateFieldErrors.city}
                    helperText={corporateFieldErrors.city || undefined}
                    sx={inputSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="PIN / ZIP"
                variant="outlined"
                size="medium"
                value={companyPincode}
                onChange={(e) => {
                  setCompanyPincode(e.target.value);
                  if (corporateFieldErrors.pincode) {
                    setCorporateFieldErrors((p) => ({ ...p, pincode: '' }));
                  }
                }}
                error={!!corporateFieldErrors.pincode}
                helperText={corporateFieldErrors.pincode || undefined}
                sx={inputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={() => dismissCorporateDialog(true)}
            sx={{ borderColor: theme.border, color: theme.textSecondary }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveCorporateDialog}
            sx={{
              bgcolor: theme.accent,
              color: theme.mode === 'dark' ? '#121212' : '#000',
              fontWeight: 600,
              '&:hover': { bgcolor: theme.accent },
            }}
          >
            Save & continue
          </Button>
        </DialogActions>
      </Dialog>

      <CheckoutResultDialog
        open={Boolean(checkoutResult)}
        result={checkoutResult}
        theme={theme}
        symbol={symbol}
        onClose={() => {
          submitIdempotencyKeyRef.current = null;
          setCheckoutResult(null);
        }}
        onViewBookings={() => {
          const bookingCode = checkoutResult?.booking_code || checkoutResult?.booking_id;
          submitIdempotencyKeyRef.current = null;
          setCheckoutResult(null);
          navigate(nexastayPath(basePath, hotelId, 'bookings'), {
            state: { fromBooking: true, bookingSuccess: true, bookingCode },
          });
        }}
      />
    </Box>
  );
}
